import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { WindowRef, WindowService, WindowSettings } from '@progress/kendo-angular-dialog';

export interface ModalWindowSettings extends WindowSettings {
  modal?: boolean;        // Enable/disable modal overlay (default:true)
  overlayClass?: string;  // Custom overlay CSS class (default: 'k-overlay)
  anchorRef?:string;      // container reference
}

@Injectable({
  providedIn: 'root'
})
export class ModalWindowService {

  private rendener: Renderer2;
  private overlayElements = new Map<WindowRef, HTMLElement>();

  constructor(
    private windowService: WindowService,
    private rendererFactory: RendererFactory2
  ) {
    this.rendener = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * Opens a window with automatic modal overlay support
   * @param settings Window configuration with optional modal settings
   * @return WindowRef with enhanced modal functionality
   */
  open(settings: ModalWindowSettings): WindowRef {
    // Default modal to true if not specified
    const modalEnabled = settings.modal !== false;
    const overlayClass = settings.overlayClass || 'k-overlay';
    const anchor = settings.anchorRef || "[kendoWindowContainer]";

    //Remove our custom properties before passing to Kendo service
    const {
      modal,
      overlayClass: customOverlayClass,
      ...kendoSettings
    } = settings;
    const windowRef = this.windowService.open(kendoSettings);

    // Add modal overlay if enabled
    if (modalEnabled) {
      this.createOverlay(windowRef, overlayClass, anchor);
    }

    // Listen for window close to clean up overlay
    windowRef.result.subscribe(() => {
      this.removeOverlay(windowRef);
    });

    return windowRef;
  }

  /**
   * Manually add overlay to an existing window
   * @param windowRef The window reference
   * @param overlayClass CSS class for the overlay
   */
  addOverlay(windowRef: WindowRef, overlayClass: string = 'k-overlay',
    anchor: string = "[kendoWindowContainer]"
  ): void {
    if (!this.overlayElements.has(windowRef)) {
      this.createOverlay(windowRef, overlayClass, anchor);
    }
  }

  /**
   * Manually remove overlay from a window
   * @param windowRef The window reference
   */
  removeOverlay(windowRef: WindowRef): void {
    const overlayElement = this.overlayElements.get(windowRef);
    if (overlayElement && overlayElement.parentNode) {
      this.rendener.removeChild(overlayElement.parentNode, overlayElement);
      this.overlayElements.delete(windowRef);
    }
  }

  /**
   * Close a window and automatically clean up its overlay
   * @param windowRef The window reference to close
   */
  close(windowRef: WindowRef): void {
    this.removeOverlay(windowRef);
    windowRef.close();
  }

  private createOverlay(
    windowRef: WindowRef,
    overlayClass: string,
    defaultAnchor: string = "[kendoWindowContainer]"
  ): void {
    // Find the window container or use document.body
    const container = this.findWindowContainer(defaultAnchor);

    // Create overlay element
    const overlayElement = this.rendener.createElement("div");

    // Calculate the current max zIndex
    let zIndx = this.calcZIndex();
    overlayElement.style.zIndex = zIndx;
    
    this.rendener.addClass(overlayElement, overlayClass);

    // Insert overlay before the window element
    // This ensure proper z-index layering
    this.rendener.appendChild(container, overlayElement);

    // Store reference for cleanup
    this.overlayElements.set(windowRef, overlayElement);

    //Optional: Add click handler to close an overlay click
    // this.rendener.listen(overlayElement, "click", (event) => {
    //   // Close window when clicking on overlay
    //   this.close(windowRef);
    //   event.stopPropagation();
    // });
  }

  private calcZIndex():number {
    const casiContainer = document.querySelector('#casi-container');
    const allElements = casiContainer!.querySelectorAll('*'); // document.querySelectorAll('app-root');

    let highestZIndex = 0;

    // Loop through elements to find the maximum z-index
    allElements.forEach(el => {
      const zIndex = window.getComputedStyle(el).zIndex;

      if(!!zIndex && zIndex !== 'auto'){
        highestZIndex = Math.max(highestZIndex, parseInt(zIndex, 10));
      }
    })
    return highestZIndex + 1;
  }

  private findWindowContainer(defaultAnchor: string = "[kendoWindowContainer]"): HTMLElement {
    // Look for kendowWindowContainer directive
    const containerElement = document.querySelector(defaultAnchor);
    if (containerElement) {
      return containerElement as HTMLElement;
    }

    // Look for common container classes
    const commonContainers = [
      ".example-wrapper",
      ".app-container",
      ".main-content",
      "my-app"
    ];

    for (const selector of commonContainers) {
      const element = document.querySelector(selector);
      if (element) {
        return element as HTMLElement;
      }
    }

    // Fallback to document.body
    return document.body;
  }
}
